import { useEffect, useRef, useState } from "react";
import {
    Building2,
    MapPin,
    User,
    FileText,
    Save,
    Upload,
    X,
    ImageIcon,
    Mail,
    Phone,
    Globe,
} from "lucide-react";
import { EMPLOYER } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

/*
 * Organization Edit screen.
 *
 * Editable fields mirror the BE `CompanySerializer`
 * (administrator/serializers.py) which is the source of truth for what the
 * `administrator/company/` PUT endpoint accepts:
 *   company_name, registration_number, business_type, profile,
 *   official_email_address, landline, website, registered_address, city,
 *   state, country, pincode, contact_person, contact_person_number, pan_no,
 *   gst_number, registration_certificate, gst_certificate.
 *
 * `prefix` and `db_name` are shown read-only — they seed employee IDs and the
 * tenant database and must not be changed after registration.
 *
 * The PUT handler reads data['profile'] / data['registration_certificate'] /
 * data['gst_certificate'] unconditionally, so those three keys are ALWAYS sent.
 * For each: send the new File to replace, or the existing value (a non-empty
 * string) to keep it untouched — the BE drops any value longer than 1 char so
 * it never overwrites the stored key with a presigned URL.
 */

const TEXT_FIELDS = [
    "company_name",
    "registration_number",
    "business_type",
    "official_email_address",
    "landline",
    "website",
    "registered_address",
    "city",
    "state",
    "country",
    "pincode",
    "contact_person",
    "contact_person_number",
    "pan_no",
    "gst_number",
];

const FILE_FIELDS = ["profile", "registration_certificate", "gst_certificate"];

/* --------------------------------- layout --------------------------------- */

const SectionCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                <Icon className="h-4.5 w-4.5" />
            </span>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        </div>
        {children}
    </div>
);

// Read-only presentation of one detail (label + value). Blank values show "—".
const ViewItem = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-2.5">
        {Icon && (
            <span className="mt-0.5 w-8 h-8 flex-shrink-0 rounded-lg bg-sidebar/10 text-sidebar-primary grid place-items-center">
                <Icon className="h-4 w-4" />
            </span>
        )}
        <div className="min-w-0">
            <div className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">{label}</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 break-words">{value || "—"}</div>
        </div>
    </div>
);

// Read-only company card shown to a branch manager (they can view, not edit).
const CompanyCard = ({ form, logoUrl }) => (
    <div className="space-y-6">
        {/* Header card: logo + name + registration/type */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 flex items-center gap-5">
            <div className="w-20 h-20 flex-shrink-0 rounded-2xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 grid place-items-center overflow-hidden">
                {logoUrl
                    ? <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain" />
                    : <Building2 className="h-8 w-8 text-gray-400" />}
            </div>
            <div className="min-w-0">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {form.company_name || "—"}
                </h2>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                    {form.business_type && <span>{form.business_type}</span>}
                    {form.registration_number && <span>Reg. No: {form.registration_number}</span>}
                </div>
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact */}
            <SectionCard icon={Mail} title="Contact">
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    <ViewItem icon={Mail} label="Official Email" value={form.official_email_address} />
                    <ViewItem icon={Phone} label="Landline" value={form.landline} />
                    <ViewItem icon={Globe} label="Website" value={form.website} />
                    <ViewItem icon={User} label="Contact Person" value={form.contact_person} />
                    <ViewItem icon={Phone} label="Contact Number" value={form.contact_person_number} />
                </div>
            </SectionCard>

            {/* Address */}
            <SectionCard icon={MapPin} title="Registered Address">
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    <ViewItem icon={MapPin} label="Address" value={form.registered_address} />
                    <ViewItem label="City" value={form.city} />
                    <ViewItem label="State" value={form.state} />
                    <ViewItem label="Country" value={form.country} />
                    <ViewItem label="Pincode" value={form.pincode} />
                </div>
            </SectionCard>

            {/* Statutory */}
            <SectionCard icon={FileText} title="Statutory">
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    <ViewItem icon={FileText} label="PAN" value={form.pan_no} />
                    <ViewItem icon={FileText} label="GST Number" value={form.gst_number} />
                </div>
            </SectionCard>
        </div>
    </div>
);

const Field = ({ label, name, value, onChange, type = "text", placeholder, disabled, hint, required }) => (
    <div className="space-y-1.5">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <Input
            type={type}
            name={name}
            value={value ?? ""}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(disabled && "opacity-60 cursor-not-allowed")}
        />
        {hint && <p className="text-[11px] text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
);

/* ------------------------------- file upload ------------------------------ */

const FileUpload = ({ label, hint, accept, previewUrl, fileName, isImage, onSelect, onClear }) => {
    const inputRef = useRef(null);
    return (
        <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {label}
            </label>
            <div className="flex items-center gap-3">
                <div className="w-16 h-16 flex-shrink-0 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 grid place-items-center overflow-hidden">
                    {isImage && previewUrl ? (
                        <img src={previewUrl} alt={label} className="w-full h-full object-cover" />
                    ) : previewUrl ? (
                        <FileText className="h-6 w-6 text-sidebar-primary" />
                    ) : (
                        <ImageIcon className="h-6 w-6 text-gray-300 dark:text-gray-600" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => inputRef.current?.click()}
                            className="border-sidebar/30 text-sidebar-primary hover:bg-sidebar/10"
                        >
                            <Upload className="h-3.5 w-3.5" />
                            {previewUrl ? "Replace" : "Upload"}
                        </Button>
                        {previewUrl && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    if (inputRef.current) inputRef.current.value = "";
                                    onClear();
                                }}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                                <X className="h-3.5 w-3.5" />
                                Remove
                            </Button>
                        )}
                    </div>
                    {fileName && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 truncate">{fileName}</p>
                    )}
                    {hint && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{hint}</p>}
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onSelect(file);
                    }}
                />
            </div>
        </div>
    );
};

/* --------------------------------- screen --------------------------------- */

const OrganizationEdit = () => {
    const { fetch, update } = useApiMethods();

    // A branch admin views Organization READ-ONLY (a card): fields disabled, no
    // Save. Only the org admin (is_admin / is_super_admin) can edit.
    const isOrgAdmin = sessionStorage.getItem('is_super_admin') === 'true';
    const readOnlyMode = !isOrgAdmin;

    const [form, setForm] = useState({});
    const [readOnly, setReadOnly] = useState({ prefix: "", db_name: "" });
    // For each file field: { existing: <s3 key/url string>, file: <File|null>, cleared: <bool> }
    const [files, setFiles] = useState({
        profile: { existing: "", file: null, cleared: false },
        registration_certificate: { existing: "", file: null, cleared: false },
        gst_certificate: { existing: "", file: null, cleared: false },
    });
    const [companyId, setCompanyId] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadCompany = async () => {
        setLoading(true);
        try {
            const response = await fetch(EMPLOYER);
            const data = response.data || {};
            setCompanyId(data.id);
            setIsActive(data.is_active ?? true);
            setReadOnly({ prefix: data.prefix || "", db_name: data.db_name || "" });

            const next = {};
            TEXT_FIELDS.forEach((key) => {
                next[key] = data[key] || "";
            });
            setForm(next);

            setFiles({
                profile: { existing: data.profile || "", file: null, cleared: false },
                registration_certificate: { existing: data.registration_certificate || "", file: null, cleared: false },
                gst_certificate: { existing: data.gst_certificate || "", file: null, cleared: false },
            });
        } catch (error) {
            console.error(error);
            ToastMsg("error", "Failed to load organization details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCompany();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const selectFile = (key, file) => {
        setFiles((prev) => ({ ...prev, [key]: { ...prev[key], file, cleared: false } }));
    };

    const clearFile = (key) => {
        setFiles((prev) => ({ ...prev, [key]: { ...prev[key], file: null, cleared: true } }));
    };

    // Preview URL for a file field: freshly-picked File wins, else the stored value.
    const previewFor = (key) => {
        const { existing, file, cleared } = files[key];
        if (file) return URL.createObjectURL(file);
        if (cleared) return "";
        return existing || "";
    };

    const validate = () => {
        if (!form.company_name?.trim()) {
            ToastMsg("error", "Company name is required");
            return false;
        }
        if (!form.official_email_address?.trim()) {
            ToastMsg("error", "Official email is required");
            return false;
        }
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.official_email_address.trim());
        if (!emailOk) {
            ToastMsg("error", "Please enter a valid official email address");
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!companyId) {
            ToastMsg("error", "Organization id not found");
            return;
        }
        if (!validate()) return;

        const payload = new FormData();
        payload.append("id", companyId);
        payload.append("is_active", isActive);

        TEXT_FIELDS.forEach((key) => {
            payload.append(key, form[key] ?? "");
        });

        // The BE reads each file key unconditionally, so always send all three.
        //  - new File  -> replaces the stored document
        //  - "cleared" -> send "" to wipe the stored document
        //  - unchanged -> send the existing value (BE drops values > 1 char and
        //                 keeps the stored key untouched)
        FILE_FIELDS.forEach((key) => {
            const { existing, file, cleared } = files[key];
            if (file) {
                payload.append(key, file);
            } else if (cleared) {
                payload.append(key, "");
            } else {
                payload.append(key, existing || "");
            }
        });

        try {
            setSaving(true);
            const res = await update(EMPLOYER, payload, true);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Organization details updated successfully");
                loadCompany();
            } else {
                ToastMsg("error", res?.message || "Failed to update organization details");
            }
        } catch (error) {
            console.error(error);
            ToastMsg("error", "Failed to update organization details");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-2xl" />
                    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-2xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                    <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                        <Building2 className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Organization</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {readOnlyMode
                                ? "Your organization's profile, contact and statutory details."
                                : "Update your organization profile, contact and statutory details."}
                        </p>
                    </div>
                </div>
                {/* Save is org-admin only; branch admins see a read-only card. */}
                {!readOnlyMode && (
                    <Button
                        variant="brand"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        <Save className="h-4 w-4" />
                        {saving ? "Saving..." : "Save Changes"}
                    </Button>
                )}
            </div>

            {/* Branch managers get a clean read-only card; org admins get the
                editable form below. */}
            {readOnlyMode ? (
                <CompanyCard form={form} logoUrl={previewFor("profile")} />
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Profile */}
                <SectionCard icon={Building2} title="Company Profile">
                    <div className="space-y-4">
                        <FileUpload
                            label="Company Logo"
                            accept="image/*"
                            isImage
                            previewUrl={previewFor("profile")}
                            fileName={files.profile.file?.name}
                            hint="PNG or JPG, square image recommended."
                            onSelect={(file) => selectFile("profile", file)}
                            onClear={() => clearFile("profile")}
                        />
                        <Field
                            label="Company Name"
                            name="company_name"
                            value={form.company_name}
                            onChange={handleChange}
                            placeholder="Enter company name"
                            required
                        />
                        <Field
                            label="Official Email"
                            name="official_email_address"
                            type="email"
                            value={form.official_email_address}
                            onChange={handleChange}
                            placeholder="info@company.com"
                            required
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Field
                                label="Company Prefix"
                                name="prefix"
                                value={readOnly.prefix}
                                disabled
                                hint="Used for employee IDs — cannot be changed."
                            />
                            <Field
                                label="Database Name"
                                name="db_name"
                                value={readOnly.db_name}
                                disabled
                                hint="System generated."
                            />
                        </div>
                    </div>
                </SectionCard>

                {/* Company Information */}
                <SectionCard icon={FileText} title="Company Information">
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Field
                                label="Business Type"
                                name="business_type"
                                value={form.business_type}
                                onChange={handleChange}
                                placeholder="e.g. Private Limited"
                            />
                            <Field
                                label="Registration Number"
                                name="registration_number"
                                value={form.registration_number}
                                onChange={handleChange}
                                placeholder="Registration number"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field
                                label="Landline"
                                name="landline"
                                value={form.landline}
                                onChange={handleChange}
                                placeholder="Landline number"
                            />
                            <Field
                                label="Website"
                                name="website"
                                value={form.website}
                                onChange={handleChange}
                                placeholder="www.company.com"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field
                                label="PAN Number"
                                name="pan_no"
                                value={form.pan_no}
                                onChange={handleChange}
                                placeholder="ABCTY1234D"
                            />
                            <Field
                                label="GST Number"
                                name="gst_number"
                                value={form.gst_number}
                                onChange={handleChange}
                                placeholder="33AAOFT3780G1ZX"
                            />
                        </div>
                    </div>
                </SectionCard>

                {/* Registered Address */}
                <SectionCard icon={MapPin} title="Registered Address">
                    <div className="space-y-4">
                        <Field
                            label="Full Address"
                            name="registered_address"
                            value={form.registered_address}
                            onChange={handleChange}
                            placeholder="Street, area, landmark"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="City" name="city" value={form.city} onChange={handleChange} placeholder="City" />
                            <Field label="State" name="state" value={form.state} onChange={handleChange} placeholder="State" />
                            <Field label="Country" name="country" value={form.country} onChange={handleChange} placeholder="Country" />
                            <Field label="Pincode" name="pincode" value={form.pincode} onChange={handleChange} placeholder="Pincode" />
                        </div>
                    </div>
                </SectionCard>

                {/* Contact & Documents */}
                <SectionCard icon={User} title="Contact & Documents">
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Field
                                label="Contact Person"
                                name="contact_person"
                                value={form.contact_person}
                                onChange={handleChange}
                                placeholder="Full name"
                            />
                            <Field
                                label="Contact Number"
                                name="contact_person_number"
                                value={form.contact_person_number}
                                onChange={handleChange}
                                placeholder="Phone number"
                            />
                        </div>
                        <FileUpload
                            label="Registration Certificate"
                            accept="image/*,application/pdf"
                            previewUrl={previewFor("registration_certificate")}
                            fileName={files.registration_certificate.file?.name}
                            hint="PDF or image."
                            onSelect={(file) => selectFile("registration_certificate", file)}
                            onClear={() => clearFile("registration_certificate")}
                        />
                        <FileUpload
                            label="GST Certificate"
                            accept="image/*,application/pdf"
                            previewUrl={previewFor("gst_certificate")}
                            fileName={files.gst_certificate.file?.name}
                            hint="PDF or image."
                            onSelect={(file) => selectFile("gst_certificate", file)}
                            onClear={() => clearFile("gst_certificate")}
                        />
                    </div>
                </SectionCard>
            </div>
            )}

            {/* Footer save (mobile-friendly) — org admin only. */}
            {!readOnlyMode && (
            <div className="flex justify-end">
                <Button
                    variant="brand"
                    onClick={handleSave}
                    disabled={saving}
                >
                    <Save className="h-4 w-4" />
                    {saving ? "Saving..." : "Save Changes"}
                </Button>
            </div>
            )}
        </div>
    );
};

export default OrganizationEdit;
